import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  CheckCircle,
  Zap,
  Users,
  Briefcase,
  Shield,
  ArrowLeft,
  Loader2,
  Star,
  Calendar,
  Lock,
  Info,
  AlertTriangle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { getBillingState, BillingSubscription } from '../../lib/billingState';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';

// ── Plan constants — both plans MUST belong to the same PayPal app ──────────
// Single client ID loaded from environment (set in Supabase secrets + .env)
const IS_SANDBOX       = import.meta.env.VITE_PAYPAL_ENV === 'sandbox';
const PAYPAL_CLIENT_ID = (IS_SANDBOX
  ? import.meta.env.VITE_PAYPAL_SANDBOX_CLIENT_ID
  : import.meta.env.VITE_PAYPAL_LIVE_CLIENT_ID) as string;
const MONTHLY_PLAN_ID  = IS_SANDBOX
  ? import.meta.env.VITE_SANDBOX_MONTHLY_PLAN_ID
  : import.meta.env.VITE_LIVE_MONTHLY_PLAN_ID;
const YEARLY_PLAN_ID   = IS_SANDBOX
  ? import.meta.env.VITE_SANDBOX_YEARLY_PLAN_ID
  : import.meta.env.VITE_LIVE_YEARLY_PLAN_ID;

type Cycle = 'monthly' | 'yearly';

const FEATURES = [
  { icon: Briefcase,   text: 'Self-service job posting with direct Workday requisition links' },
  { icon: Users,       text: '3 included team seats — add more for $99.99 each' },
  { icon: CheckCircle, text: 'Direct candidate messaging & pipeline tracking' },
  { icon: Star,        text: 'Pro job postings — reveal your company name to top talent' },
  { icon: Shield,      text: 'Full Pro access continues through grace period if you cancel' },
  { icon: Zap,         text: 'Browse full Workday talent pool with contact info' },
];

const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS  = 2000;

export default function UpgradePro() {
  const navigate     = useNavigate();
  const { user }     = useAuth();

  const [cycle, setCycle]           = useState<Cycle>('monthly');
  const [sdkState, setSdkState]     = useState<'loading' | 'ready' | 'error'>('loading');
  const [btnState, setBtnState]     = useState<'idle' | 'rendered' | 'error'>('idle');
  const [toast, setToast]           = useState<{ type: 'info' | 'error'; msg: string } | null>(null);
  const [awaitingActivation, setAwaitingActivation] = useState(false);

  // Track the currently rendered plan so we know when to re-render
  const renderedCycle  = useRef<Cycle | null>(null);
  const pollStopped    = useRef(false);
  const CONTAINER_ID   = 'paypal-sub-btn';

  const showToast = (type: 'info' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 5000);
  };

  // Stop any in-flight poll on unmount
  useEffect(() => { return () => { pollStopped.current = true; }; }, []);

  // Poll billing_subscriptions until getBillingState() === 'PRO_ACTIVE'.
  // All field comparisons are encapsulated in getBillingState() — this call site is clean.
  const waitForActivation = useCallback(async (_currentCycle: Cycle): Promise<boolean> => {
    pollStopped.current = false;
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      if (pollStopped.current) return false;
      const { data } = await supabase
        .from('billing_subscriptions')
        .select('*')
        .eq('user_id', user!.id)
        .maybeSingle();
      if (getBillingState(data as BillingSubscription | null) === 'PRO_ACTIVE') {
        return true;
      }
      await new Promise<void>((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    return false;
  }, [user]);

  // ── Load PayPal SDK exactly once using the single client ID ───────────────
  useEffect(() => {
    if (!PAYPAL_CLIENT_ID) {
      console.error('[PayPal] VITE_PAYPAL_CLIENT_ID is not set');
      setSdkState('error');
      return;
    }

    // If SDK already present from a prior mount, use it
    if ((window as any).paypal) {
      setSdkState('ready');
      return;
    }

    const existing = document.querySelector('script[data-paypal-upgrade]');
    if (existing) {
      // Script injected but not yet loaded — wait for it
      existing.addEventListener('load', () => setSdkState('ready'));
      existing.addEventListener('error', () => setSdkState('error'));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
    script.setAttribute('data-sdk-integration-source', 'button-factory');
    script.setAttribute('data-paypal-upgrade', 'true');
    script.onload  = () => setSdkState('ready');
    script.onerror = () => {
      console.error('[PayPal] SDK script failed to load');
      setSdkState('error');
    };
    document.body.appendChild(script);
  }, []); // intentionally empty — load once for the lifetime of this page

  // ── Render / re-render button when SDK is ready or cycle changes ──────────
  useEffect(() => {
    if (sdkState !== 'ready') return;
    if (!user?.id) return; // never render without a user ID — custom_id would be empty

    // Skip re-render if same cycle already rendered
    if (renderedCycle.current === cycle && btnState === 'rendered') return;

    const pp = (window as any).paypal;
    if (!pp) {
      setSdkState('error');
      return;
    }

    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    // Wipe previous button instance
    container.innerHTML = '';
    renderedCycle.current = null;
    setBtnState('idle');

    const planId = cycle === 'yearly' ? YEARLY_PLAN_ID : MONTHLY_PLAN_ID;
    const userId = user.id; // captured in closure — guaranteed non-empty

    console.log(`[PayPal] Rendering button — plan_id: ${planId}, user_id: ${userId}`);

    try {
      pp.Buttons({
        style: { shape: 'rect', color: 'blue', layout: 'vertical', label: 'subscribe' },

        createSubscription: (_data: unknown, actions: Record<string, Function>) => {
          console.log(`[PayPal] createSubscription — plan_id: ${planId}, custom_id: ${userId}`);
          return actions.subscription.create({
            plan_id: planId,
            custom_id: userId, // CRITICAL: maps subscription back to this user in webhook
          });
        },

        onApprove: async (data: { subscriptionID: string }) => {
          console.log(`[PayPal] onApprove — subscriptionID: ${data.subscriptionID}, custom_id: ${userId}, cycle: ${cycle}`);
          // PayPal fires onApprove when the user clicks Subscribe — the webhook has
          // NOT arrived yet. Poll until billing_subscriptions.activated_at is set
          // before navigating, so Pro access is real before the success page shows.
          setAwaitingActivation(true);
          const confirmed = await waitForActivation(cycle);
          if (pollStopped.current) return;
          setAwaitingActivation(false);
          navigate(
            `/workday-client/billing/success?subscription_id=${encodeURIComponent(data.subscriptionID)}&cycle=${cycle}${confirmed ? '' : '&pending=1'}`
          );
        },

        onCancel: () => {
          showToast('info', 'Checkout cancelled — no charge was made.');
        },

        onError: (err: unknown) => {
          console.error('[PayPal] Button error:', err);
          setBtnState('error');
          showToast('error', 'PayPal encountered an error. Please try again or reload the page.');
        },
      }).render(`#${CONTAINER_ID}`);

      renderedCycle.current = cycle;
      setBtnState('rendered');
    } catch (err) {
      console.error('[PayPal] Render threw:', err);
      setBtnState('error');
    }
  }, [sdkState, cycle, user, navigate]);

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <WorkdayClientHeader hideNav logoSrc="/perplexity-new-logo.png" />

      {/* Activation polling overlay */}
      {awaitingActivation && (
        <div className="fixed inset-0 z-50 bg-white/90 flex flex-col items-center justify-center gap-5 text-center px-6">
          <Loader2 className="w-12 h-12 text-cyan-500 animate-spin" />
          <p className="text-gray-900 text-lg font-semibold">Confirming your subscription…</p>
          <p className="text-gray-700 text-sm max-w-xs">
            PayPal confirmed your payment. Waiting for account activation — this takes 5–20 seconds.
          </p>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-start gap-3 px-5 py-4 rounded-xl shadow-2xl border max-w-sm text-sm font-medium ${
          toast.type === 'error'
            ? 'bg-red-50 border-red-200 text-red-700'
            : 'bg-amber-50 border-amber-200 text-amber-700'
        }`}>
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {toast.msg}
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 pt-28 pb-24">

        {/* Back */}
        <button
          onClick={() => navigate('/workday-client/dashboard')}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Cancel
        </button>

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-600 text-xs font-semibold uppercase tracking-widest mb-5">
            <Zap className="w-3.5 h-3.5" /> Upgrade to Pro
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">
            Unlock the full platform
          </h1>
          <p className="text-gray-700 text-base max-w-xl mx-auto">
            Self-service hiring, direct talent access, team seats, and candidate pipeline — everything your team needs to move faster.
          </p>
        </div>

        {/* Billing cycle toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-gray-100 border border-gray-200 rounded-xl p-1 gap-1">
            <button
              onClick={() => setCycle('monthly')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                cycle === 'monthly'
                  ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setCycle('yearly')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                cycle === 'yearly'
                  ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Yearly
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

          {/* ── Left: Plan details ───────────────────────────────────────── */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 pt-6 pb-5 border-b border-gray-100">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-cyan-600 mb-1">Pro Plan</p>
                  <h2 className="text-2xl font-extrabold text-gray-900">
                    {cycle === 'yearly' ? '$5,988' : '$499'}
                    <span className="text-base font-normal text-gray-600 ml-1">
                      /{cycle === 'yearly' ? 'year' : 'mo'}
                    </span>
                  </h2>
                  <p className="text-gray-600 text-xs mt-1">
                    {cycle === 'yearly' ? 'Billed annually, cancel anytime' : 'Billed monthly, cancel anytime'}
                  </p>
                </div>
                <div className="p-2.5 rounded-xl bg-cyan-50 border border-cyan-200">
                  <Star className="w-5 h-5 text-cyan-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Users,    label: 'Seats',    value: '3 included',    sub: '+$99.99 each extra' },
                  { icon: Briefcase,label: 'Job Posts', value: '3 free',        sub: 'Buy credit packs anytime' },
                  { icon: Calendar, label: 'Billing',   value: cycle === 'yearly' ? 'Yearly' : 'Monthly', sub: 'Cancel anytime' },
                  { icon: Shield,   label: 'Grace',     value: 'Full cycle',    sub: 'Access after cancel' },
                ].map(({ icon: Icon, label, value, sub }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon className="w-3.5 h-3.5 text-cyan-500" />
                      <p className="text-gray-600 text-xs uppercase tracking-wide">{label}</p>
                    </div>
                    <p className="text-gray-900 font-bold text-sm">{value}</p>
                    <p className="text-gray-600 text-xs">{sub}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-5 space-y-3">
              {FEATURES.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-start gap-3">
                  <div className="p-1 rounded-md bg-cyan-50 flex-shrink-0 mt-0.5">
                    <Icon className="w-3.5 h-3.5 text-cyan-500" />
                  </div>
                  <p className="text-gray-800 text-sm leading-snug">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Checkout ──────────────────────────────────────────── */}
          <div className="sticky top-8 space-y-4">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Complete your upgrade</h3>
              <p className="text-gray-700 text-sm mb-6">
                Secure checkout via PayPal. Your subscription activates automatically within seconds of payment.
              </p>

              {/* Order summary */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 mb-6 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-700 text-sm">Plan</span>
                  <span className="text-gray-900 text-sm font-semibold">PerplexityATS Pro</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 text-sm">Billing cycle</span>
                  <span className="text-gray-900 text-sm font-semibold capitalize">{cycle}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700 text-sm">Seats included</span>
                  <span className="text-gray-900 text-sm font-semibold">3</span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                  <span className="text-gray-700 text-sm font-semibold">Total today</span>
                  <span className="text-cyan-600 font-extrabold text-lg">
                    {cycle === 'yearly' ? '$5,988.00' : '$499.00'}
                  </span>
                </div>
              </div>

              {/* PayPal button area */}
              <div className="min-h-[56px]">
                {sdkState === 'loading' && (
                  <div className="flex items-center justify-center gap-2 py-4 text-gray-600 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading secure checkout…
                  </div>
                )}

                {sdkState === 'error' && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-amber-700 text-xs">
                        Secure checkout could not load. Please reload the page and try again. If the problem persists, try a different browser or disable any ad blockers.
                      </p>
                    </div>
                    <button
                      onClick={() => window.location.reload()}
                      className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl bg-gray-100 border border-gray-200 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-colors"
                    >
                      <Loader2 className="w-4 h-4" />
                      Reload page
                    </button>
                  </div>
                )}

                {btnState === 'error' && sdkState === 'ready' && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-xs">
                      PayPal button failed to render. Please reload the page and try again.
                    </p>
                  </div>
                )}

                <div
                  id={CONTAINER_ID}
                  className={sdkState !== 'ready' ? 'hidden' : ''}
                />
              </div>

              {/* Trust row */}
              <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center justify-center gap-4">
                <div className="flex items-center gap-1.5 text-gray-600 text-xs">
                  <Shield className="w-3.5 h-3.5" /> SSL encrypted
                </div>
                <div className="flex items-center gap-1.5 text-gray-600 text-xs">
                  <CheckCircle className="w-3.5 h-3.5" /> Cancel anytime
                </div>
                <div className="flex items-center gap-1.5 text-gray-600 text-xs">
                  <Lock className="w-3.5 h-3.5" /> Secured by PayPal
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="space-y-2">
              {[
                { q: 'When does access activate?', a: 'Within seconds — PayPal sends a webhook that instantly activates your plan.' },
                { q: 'What if I cancel?', a: 'Pro features stay active until end of your current billing period (grace period).' },
                { q: 'Can I switch plans?', a: 'Yes — cancel your current plan and re-subscribe to switch billing cycles.' },
              ].map(({ q, a }) => (
                <div key={q} className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
                  <p className="text-gray-800 text-xs font-semibold mb-0.5">{q}</p>
                  <p className="text-gray-700 text-xs">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
